
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Search, Container, Dropdown, Message } from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { GET_TAGS } from '../../../../queries/tags';
import { usePeopleSearch } from '../shared/usePeopleSearch';
import { PersonListItem } from '../shared/PersonListItem';
import { PeopleListSkeleton, SearchSkeleton } from '../shared/PeopleSkeleton';

import styles from './AdminPeopleView.module.css';

const AdminPeopleView = () => {
    const navigate = useNavigate();
    const {
        search,
        tagIds,
        hasTagFilter,
        loading,
        data,
        handleSearchChange,
        handleTagsChange,
        shouldShowSearch,
    } = usePeopleSearch();

    // Same arguments PersonTags uses — people tags only, so course tags don't clutter
    // a list of people.
    const { data: tagData } = useQuery(GET_TAGS, {
        variables: { showPeopleTags: true, showCourseTags: false },
    });

    const tagOptions = (tagData?.tags ?? []).map((tag: any) => ({
        key: tag.id,
        text: tag.name,
        value: tag.id,
    }));

    // Show placeholder rows whenever we're loading with nothing to display yet —
    // on first load, or while a new search resolves. The real search field is
    // never swapped for a skeleton once it's on screen (that would steal focus);
    // the skeleton bar only fills the otherwise-blank initial-load gap.
    const showListSkeleton = loading && !data?.users?.length;

    return (
        <ResponsiveContainer>
            <Container style={{ paddingBottom: '3em' }}>
                <h2 className={styles.pageTitle}>People</h2>

                {shouldShowSearch ? (
                    <div className={styles.filterRow}>
                        <div className={styles.filterSearch}>
                            <Search
                                loading={loading}
                                input={{ fluid: true }}
                                size="large"
                                placeholder="Search..."
                                open={false}
                                onSearchChange={handleSearchChange}
                                value={search}
                            />
                        </div>
                        <Dropdown
                            className={styles.filterTags}
                            placeholder="Filter by tag"
                            fluid
                            selection
                            multiple
                            search
                            clearable
                            options={tagOptions}
                            value={tagIds}
                            onChange={(_, { value }) => handleTagsChange((value as number[]) ?? [])}
                        />
                        {tagIds.length > 1 && (
                            <p className={styles.filterHint}>
                                Showing people with <strong>all</strong> of the selected tags.
                            </p>
                        )}
                    </div>
                ) : loading ? (
                    <div className={styles.searchWrap}>
                        <SearchSkeleton />
                    </div>
                ) : null}

                <div>
                    {showListSkeleton
                        ? <PeopleListSkeleton count={8} showTags />
                        : data?.users.map((p: any) => (
                            <PersonListItem
                                key={p.id}
                                person={p}
                                onClick={() => navigate(`/people/${p.id}`)}
                                showTags
                            />
                        ))}
                </div>

                {!loading && data?.users.length === 0 && hasTagFilter && (
                    <Message style={{ marginTop: '1em' }}>
                        <Message.Header>Nobody matches that filter</Message.Header>
                        <p>
                            {tagIds.length > 1
                                ? 'Nobody has all of the selected tags. Try removing one.'
                                : 'Nobody here has that tag.'}
                        </p>
                    </Message>
                )}

                {shouldShowSearch && !loading && data?.users.length > 10 && (
                    <Message style={{ marginTop: '1em' }}>
                        <Message.Header>Don't see who you're looking for?</Message.Header>
                        <p>Try a more specific search — we don't load everyone at once.</p>
                    </Message>
                )}
            </Container>
        </ResponsiveContainer>
    );
};

export { AdminPeopleView };
